import { useEffect, useState } from "react";
import styled from "styled-components";
import { Download } from "lucide-react";

import Panel from "../../../components/PartnerDashboard/Panel";
import Skeleton from "../../../components/PartnerDashboard/Skeleton";
import {
  Badge,
  BadgeTone,
  Button,
  Empty,
  ErrorText,
  Note,
  PrimaryText,
  SearchInput,
  SecondaryText,
  Select,
  TableScroll,
  Td,
  Th,
  Toolbar,
} from "../../../components/PartnerDashboard/ui";
import { adminGetBookingsList } from "../../../api/apiEndpoints";
import { formatCurrency } from "../../../utils/partnerMetrics";
import { downloadCsv } from "../../../utils/reportDates";
import { ActionRow, StackTable } from "./ui";

interface Booking {
  id: string;
  classStartTime: string;
  bookedAt: string;
  studio: string | null;
  className: string | null;
  memberName: string | null;
  email: string | null;
  type: "regular" | "drop";
  status: string;
  feeCents: number;
  feeLabel: string | null;
}

const PAGE_SIZE = 25;

const STATUS: Record<string, { label: string; tone: BadgeTone }> = {
  attended: { label: "Attended", tone: "green" },
  booked: { label: "Upcoming", tone: "purple" },
  no_show: { label: "No-show", tone: "red" },
  late_cancelled: { label: "Late cancel", tone: "amber" },
  cancelled: { label: "Cancelled", tone: "gray" },
};

const TYPE_LABEL = { regular: "Regular", drop: "Class drop" };

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
  font-family: 'Aileron', 'Poppins', sans-serif;
  font-size: 13px;
  color: #6b6259;
`;

const easternTime = (iso: string) =>
  new Date(iso).toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const statusLabel = (s: string) => STATUS[s]?.label ?? s;

interface Props {
  start: string;
  end: string;
  businessIds?: string;
  basis: "booked" | "class";
  invalid: boolean;
  fileSuffix: string;
}

const BookingsTable = ({ start, end, businessIds, basis, invalid, fileSuffix }: Props) => {
  const [bookings, setBookings] = useState<Booking[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (invalid) return;
    let cancelled = false;
    setBookings(null);
    setError(null);
    setPage(1);
    adminGetBookingsList({ start, end, businessIds, basis })
      .then((res) => {
        if (cancelled) return;
        setBookings(res.data.bookings);
        setTruncated(res.data.truncated);
      })
      .catch(() => !cancelled && setError("Couldn't load bookings. Please try again."));
    return () => {
      cancelled = true;
    };
  }, [start, end, businessIds, basis, invalid]);

  const q = search.trim().toLowerCase();
  const filtered = (bookings ?? []).filter(
    (b) =>
      (!type || b.type === type) &&
      (!status || b.status === status) &&
      (!q ||
        (b.memberName ?? "").toLowerCase().includes(q) ||
        (b.email ?? "").toLowerCase().includes(q) ||
        (b.studio ?? "").toLowerCase().includes(q) ||
        (b.className ?? "").toLowerCase().includes(q)),
  );
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const rows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const download = () =>
    downloadCsv(
      [
        ["Class date (ET)", "Studio", "Class", "Member", "Email", "Type", "Status", "Fee", "Fee note", "Booked on (ET)"],
        ...filtered.map((b) => [
          easternTime(b.classStartTime),
          b.studio,
          b.className,
          b.memberName,
          b.email,
          TYPE_LABEL[b.type],
          statusLabel(b.status),
          b.feeCents ? (b.feeCents / 100).toFixed(2) : "",
          b.feeLabel,
          easternTime(b.bookedAt),
        ]),
      ],
      `Bookings ${fileSuffix}.csv`,
    );

  const resetPage = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setPage(1);
  };

  return (
    <Panel
      title="Bookings"
      subtitle={bookings ? `${filtered.length.toLocaleString()} of ${bookings.length.toLocaleString()} bookings` : undefined}
      info="Every booking in the period and studios selected above. Class names are only saved for class drops right now."
      action={
        <Button disabled={!bookings || filtered.length === 0} onClick={download}>
          <Download size={15} />
          CSV
        </Button>
      }
    >
      <Toolbar>
        <SearchInput
          placeholder="Search member, email, studio or class…"
          value={search}
          onChange={(e) => resetPage(setSearch)(e.target.value)}
        />
        <ActionRow>
          <Select value={type} onChange={(e) => resetPage(setType)(e.target.value)} aria-label="Booking type">
            <option value="">All types</option>
            <option value="regular">Regular</option>
            <option value="drop">Class drops</option>
          </Select>
          <Select value={status} onChange={(e) => resetPage(setStatus)(e.target.value)} aria-label="Status">
            <option value="">All statuses</option>
            {Object.entries(STATUS).map(([key, s]) => (
              <option key={key} value={key}>{s.label}</option>
            ))}
          </Select>
        </ActionRow>
      </Toolbar>

      {error ? (
        <ErrorText>{error}</ErrorText>
      ) : !bookings ? (
        <Skeleton $h="240px" $radius="12px" />
      ) : filtered.length === 0 ? (
        <Empty>No bookings match these filters.</Empty>
      ) : (
        <TableScroll>
          <StackTable>
            <thead>
              <tr>
                <Th>Class</Th>
                <Th>Member</Th>
                <Th>Type</Th>
                <Th>Status</Th>
                <Th $align="right">Fee</Th>
                <Th>Booked on</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id}>
                  <Td>
                    <PrimaryText>{b.studio ?? "Unknown studio"}</PrimaryText>
                    <SecondaryText>
                      {easternTime(b.classStartTime)}
                      {b.className ? ` · ${b.className}` : ""}
                    </SecondaryText>
                  </Td>
                  <Td data-label="Member">
                    <div>
                      <div>{b.memberName ?? "—"}</div>
                      <SecondaryText>{b.email ?? ""}</SecondaryText>
                    </div>
                  </Td>
                  <Td data-label="Type">{TYPE_LABEL[b.type]}</Td>
                  <Td data-label="Status">
                    <Badge $tone={STATUS[b.status]?.tone ?? "gray"}>{statusLabel(b.status)}</Badge>
                  </Td>
                  <Td data-label="Fee" $align="right" $muted={!b.feeCents}>
                    <div>
                      {b.feeCents ? formatCurrency(b.feeCents / 100) : "—"}
                      {b.feeLabel && <SecondaryText>{b.feeLabel}</SecondaryText>}
                    </div>
                  </Td>
                  <Td data-label="Booked on" $muted>{easternTime(b.bookedAt)}</Td>
                </tr>
              ))}
            </tbody>
          </StackTable>
        </TableScroll>
      )}

      {pageCount > 1 && (
        <Pager>
          <Button disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>
            ← Prev
          </Button>
          <span>Page {currentPage} of {pageCount}</span>
          <Button disabled={currentPage === pageCount} onClick={() => setPage(currentPage + 1)}>
            Next →
          </Button>
        </Pager>
      )}
      {truncated && (
        <Note>Showing the most recent 20,000 bookings. Pick a shorter period to see the rest.</Note>
      )}
    </Panel>
  );
};

export default BookingsTable;
